import { computed, getCurrentInstance, unref, isRef } from 'vue';
import { MaybeRef } from '@vueuse/core';

export function createSyncRef(key: string) {
  const instance = getCurrentInstance();

  if (!instance) {
    return undefined as any;
  }

  return computed({
    get() {
      return instance.props[key];
    },
    set(v) {
      return instance.emit(`update:${key}`, v);
    },
  });
}

const isObject = (val: any) => val !== null && typeof val === 'object';
const isArray = Array.isArray;

/**
 * Unref a value, recursing into it if it's an object.
 *
 * @param {MaybeRef<any>} val - The value to unref.
 *
 * @return {Array<any>}
 */
const smartUnref = (val: MaybeRef<any>) => {
  // Non-ref object?  Go deeper!
  if (val !== null && !isRef(val) && typeof val === 'object') {
    return deepUnref(val);
  }

  return unref(val);
};

/**
 * Unref an array, recursively.
 *
 * @param {Array} arr - The array to unref.
 *
 * @return {Array}
 */
const unrefArray = (arr: Array<any>): Array<any> => arr.map(smartUnref);

/**
 * Unref an object, recursively.
 *
 * @param {MaybeRef<any>} obj - The object to unref.
 *
 * @return {Object}
 */
const unrefObject = (obj: MaybeRef<any>) => {
  const unrefed = {} as Record<string, any>;

  // Object? un-ref it!
  Object.keys(obj).forEach((key) => {
    unrefed[key] = smartUnref(obj[key]);
  });

  return unrefed;
};

/**
 * Deeply unref a value, recursing into objects and arrays.
 *
 * @param {MaybeRef<any>} val - The value to deeply unref.
 *
 * @return {Object}
 */
export function deepUnref(val: MaybeRef<any>) {
  const checkedVal = isRef(val) ? unref(val) : val;

  if (!isObject(checkedVal)) {
    return checkedVal;
  }

  if (isArray(checkedVal)) {
    return unrefArray(checkedVal);
  }

  return unrefObject(checkedVal);
}
